import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Calendar, CalendarClock, Settings, Trophy, MapPin, KeyRound, ListChecks, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { username, userRole, logout } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="text-2xl font-bold text-blue-600 cursor-pointer flex items-center gap-2"
          >
            🎰 Moker
          </div>

          {/* Desktop Menu */}
          <ul className="hidden md:flex space-x-8 items-center">
            <li>
              <button
                onClick={() => navigate('/')}
                className="hover:text-blue-600 transition"
              >
                Accueil
              </button>
            </li>
            <li>
              <button
                onClick={() => navigate('/calendar')}
                className="hover:text-blue-600 transition flex items-center gap-1"
              >
                <Calendar size={18} />
                Calendrier
              </button>
            </li>
            <li>
              <button
                onClick={() => navigate('/my-schedule')}
                className="hover:text-blue-600 transition flex items-center gap-1"
              >
                <CalendarClock size={18} />
                Mon programme
              </button>
            </li>
            <li>
              <button
                onClick={() => navigate('/my-participations')}
                className="hover:text-blue-600 transition flex items-center gap-1"
              >
                <ListChecks size={18} />
                Mes participations
              </button>
            </li>
            {userRole === 'admin' && (
            <li>
              <button
                onClick={() => navigate('/leaderboard')}
                className="hover:text-blue-600 transition flex items-center gap-1"
              >
                <Trophy size={18} />
                Classement
              </button>
            </li>
            )}
            <li>
              <button
                onClick={() => navigate('/venues-map')}
                className="hover:text-blue-600 transition flex items-center gap-1"
              >
                <MapPin size={18} />
                Carte des lieux
              </button>
            </li>
            {userRole === 'admin' && (
              <>
                <li>
                  <button
                    onClick={() => navigate('/admin')}
                    className="hover:text-blue-600 transition flex items-center gap-1 font-semibold"
                  >
                    <Settings size={18} />
                    Administration
                  </button>
                </li>
              </>
            )}
            <li className="flex items-center space-x-4">
              <span className="text-gray-700">Bonjour, {username}</span>
              {userRole === 'admin' && (
                <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded">
                  Admin
                </span>
              )}
              <button
                onClick={() => navigate('/profile')}
                className="text-gray-500 hover:text-blue-600 transition"
                title="Mon profil"
              >
                <User size={18} />
              </button>
              <button
                onClick={() => navigate('/change-password')}
                className="text-gray-500 hover:text-blue-600 transition"
                title="Changer mon mot de passe"
              >
                <KeyRound size={18} />
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
              >
                Déconnexion
              </button>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button onClick={toggleMenu} className="md:hidden">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <ul className="md:hidden mt-4 space-y-2 pb-4">
            <li>
              <button
                onClick={() => { navigate('/'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600"
              >
                Accueil
              </button>
            </li>
            <li>
              <button
                onClick={() => { navigate('/calendar'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2"
              >
                <Calendar size={18} />
                Calendrier
              </button>
            </li>
            <li>
              <button
                onClick={() => { navigate('/my-schedule'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2"
              >
                <CalendarClock size={18} />
                Mon programme
              </button>
            </li>
            <li>
              <button
                onClick={() => { navigate('/my-participations'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2"
              >
                <ListChecks size={18} />
                Mes participations
              </button>
            </li>
            {userRole === 'admin' && (
            <li>
              <button
                onClick={() => { navigate('/leaderboard'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2"
              >
                <Trophy size={18} />
                Classement
              </button>
            </li>
            )}
            <li>
              <button
                onClick={() => { navigate('/venues-map'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2"
              >
                <MapPin size={18} />
                Carte des lieux
              </button>
            </li>
            {userRole === 'admin' && (
              <>
                <li className="border-t border-gray-200 pt-2 mt-2">
                  <p className="text-xs text-gray-500 font-semibold mb-2">ADMINISTRATION</p>
                </li>
                <li>
                  <button
                    onClick={() => { navigate('/admin'); toggleMenu(); }}
                    className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2 font-semibold"
                  >
                    <Settings size={18} />
                    Administration
                  </button>
                </li>
              </>
            )}
            <li className="py-2 text-gray-700 flex items-center gap-2 border-t border-gray-200 mt-2 pt-2">
              Bonjour, {username}
              {userRole === 'admin' && (
                <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded">
                  Admin
                </span>
              )}
            </li>
            <li>
              <button
                onClick={() => { navigate('/profile'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2"
              >
                <User size={18} />
                Mon profil
              </button>
            </li>
            <li>
              <button
                onClick={() => { navigate('/change-password'); toggleMenu(); }}
                className="block py-2 w-full text-left hover:text-blue-600 flex items-center gap-2"
              >
                <KeyRound size={18} />
                Changer mon mot de passe
              </button>
            </li>
            <li>
              <button
                onClick={() => { handleLogout(); toggleMenu(); }}
                className="w-full text-left py-2 text-red-600 hover:text-red-700"
              >
                Déconnexion
              </button>
            </li>
          </ul>
        )}
      </nav>
    </header>
  );
};

export default Header;